import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { AlertTriangle, Lock, CreditCard, X, Bell } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context for cotisation status
const CotisationContext = createContext(null);

export const useCotisation = () => useContext(CotisationContext);

export function CotisationProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/auth/cotisation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Error checking access status:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
    // Refresh status every 5 minutes
    const interval = setInterval(checkStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const refresh = () => checkStatus();

  return (
    <CotisationContext.Provider value={{ status, loading, refresh }}>
      {children}
    </CotisationContext.Provider>
  );
}

// Warning Banner Component
export function CotisationBanner() {
  const { status, loading } = useCotisation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (loading || !status || dismissed) return null;

  const { blocking_status, cotisation } = status;

  // If blocked - show prominent blocking banner
  if (blocking_status?.blocked) {
    return (
      <div className="bg-red-600 text-white px-4 py-3 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-bold">Accès limité - Cotisation non payée</p>
              <p className="text-sm text-red-100">
                {blocking_status.reason}. Vous avez accès uniquement aux modules de régularisation.
              </p>
            </div>
          </div>
          <Button 
            onClick={() => navigate("/mes-paiements")}
            className="bg-white text-red-600 hover:bg-red-50"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Régulariser
          </Button>
        </div>
      </div>
    );
  }

  // If warning (relances sent but not yet blocked)
  if (blocking_status?.warning || (cotisation?.relances_envoyees > 0 && !cotisation?.payee)) {
    const relances = cotisation?.relances_envoyees || blocking_status?.relances_envoyees || 0;
    
    const getBannerColor = () => {
      if (relances >= 3) return "bg-orange-500";
      if (relances >= 2) return "bg-yellow-500";
      return "bg-amber-400";
    };

    const getTextColor = () => {
      if (relances >= 2) return "text-white";
      return "text-amber-900";
    };

    return (
      <div className={`${getBannerColor()} ${getTextColor()} px-4 py-2`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div className="flex items-center gap-2">
              <span className="font-medium">
                Cotisation {cotisation?.annee || new Date().getFullYear()} en attente
              </span>
              <span className="text-sm opacity-90">
                ({relances}/3 relance{relances > 1 ? 's' : ''} envoyée{relances > 1 ? 's' : ''})
              </span>
              {relances >= 3 && (
                <span className="text-sm font-bold ml-2">
                  ⚠️ Blocage le 1er février !
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm"
              onClick={() => navigate("/mes-paiements")}
              className={relances >= 2 ? "bg-white text-orange-600 hover:bg-orange-50" : "bg-amber-600 text-white hover:bg-amber-700"}
            >
              <CreditCard className="w-4 h-4 mr-1" />
              Payer
            </Button>
            <button 
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-black/10 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// Access Guard Component - wraps protected content
export function AccessGuard({ moduleId, children, fallback = null }) {
  const { status, loading } = useCotisation();

  if (loading) {
    return fallback || <div className="animate-pulse bg-gray-200 h-32 rounded" />;
  }

  if (!status) return children;

  const { blocking_status } = status;

  // Not blocked - allow access
  if (!blocking_status?.blocked) {
    return children;
  }

  // Check if this module is accessible when blocked
  const canAccess = blocking_status.can_access_modules?.includes(moduleId);

  if (canAccess) {
    return children;
  }

  // Show blocked message
  return (
    <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
      <Lock className="w-16 h-16 text-red-400 mb-4" />
      <h3 className="text-xl font-bold text-gray-700 mb-2">Accès restreint</h3>
      <p className="text-gray-500 text-center mb-4 max-w-md">
        Vous devez régulariser votre cotisation pour accéder à cette fonctionnalité.
      </p>
      <Button onClick={() => window.location.href = "/mes-paiements"} className="bg-red-600 hover:bg-red-700">
        <CreditCard className="w-4 h-4 mr-2" />
        Régulariser ma cotisation
      </Button>
    </div>
  );
}

export default CotisationBanner;
